import styled, { css } from 'styled-components';
import React from 'react';
import { magicNumbersForStyling } from '../magicNumbersForStyling';

const {
  rowHeight,
  nameCellWidth,
  columnWidth,
  narrowColumnWidth,
  wideColumnWidth,
  numberColumnWidth,
  toColumnWidth,
  payeeColumnWidth,
} = magicNumbersForStyling;

export const amountContentStyles = css`
  justify-content: flex-end;
  border-right: 1px solid ${(props) => props.theme.colors.gray};
`;

export const ContentCell = styled.div<{ extendedContent?: React.ReactNode }>`
  align-items: center;
  ${(props) => (!!props.extendedContent ? `` : `border-bottom: 1px solid ${props.theme.colors.gray}`)};
  display: flex;
  flex-direction: row;
  ${(props) => props.theme.fonts.mixins.body}
  line-height: 20px;
  min-height: ${rowHeight};
  padding: ${(props) => props.theme.spacing.spacing2};
`;

export const CostDataHeaderCell = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.gray};
  font-family:
    Open Sans,
    sans-serif;
  font-size: 9px;
  font-weight: bold;
  min-height: ${rowHeight};
  padding: ${(props) => `${props.theme.spacing.spacing4} ${props.theme.spacing.spacing2}`};
  text-transform: uppercase;
  &:last-child {
    border-right-width: 0;
  }
`;

interface ICostDataHeaderProps {
  showPayoff?: boolean;
  showSeller?: boolean;
}

export const CostDataHeader = styled.div<ICostDataHeaderProps>`
  display: grid;
  grid-template-columns: ${(props) =>
    `${numberColumnWidth} ${nameCellWidth} repeat(${
      props.showSeller ? '5' : '3'
    }, ${columnWidth}) ${narrowColumnWidth} ${wideColumnWidth} ${narrowColumnWidth}`};
  grid-template-areas: ${(props) =>
    `'row-number name buyer-closing buyer-before ${
      props.showSeller ? 'seller-closing seller-before' : ''
    } others netted-out lender-paid delete'`};
  ${(props) =>
    props.showPayoff &&
    `grid-template-columns: ${numberColumnWidth} ${nameCellWidth} ${toColumnWidth} ${payeeColumnWidth} ${
      props.showSeller ? columnWidth + ' ' + columnWidth : ''
    } ${columnWidth} ${wideColumnWidth} ${narrowColumnWidth} ${wideColumnWidth} ${narrowColumnWidth};
      grid-template-areas: 'row-number name to payee ${
        props.showSeller ? 'seller-closing seller-before' : ''
      } empty amount netted-out lender-paid delete';
    `}
`;

export const RowNumberHeader = styled(CostDataHeaderCell)`
  grid-area: row-number;
`;

export const NameHeader = styled(CostDataHeaderCell)`
  grid-area: name;
  padding-left: ${(props) => props.theme.spacing.spacing4};
`;

export const ToHeader = styled(CostDataHeaderCell)`
  grid-area: to;
`;

export const PayeeHeader = styled(CostDataHeaderCell)`
  grid-area: payee;
`;

export const BuyerClosingHeader = styled(CostDataHeaderCell)`
  ${amountContentStyles};
  grid-area: buyer-closing;
  text-align: right;
`;

export const BuyerBeforeHeader = styled(CostDataHeaderCell)`
  ${amountContentStyles};
  grid-area: buyer-before;
  text-align: right;
`;

export const SellerClosingHeader = styled(CostDataHeaderCell)`
  ${amountContentStyles};
  grid-area: seller-closing;
  text-align: right;
`;

export const SellerBeforeHeader = styled(CostDataHeaderCell)`
  ${amountContentStyles};
  grid-area: seller-before;
  text-align: right;
`;

export const OthersHeader = styled(CostDataHeaderCell)`
  ${amountContentStyles};
  grid-area: others;
  text-align: right;
`;

export const EmptyCellHeader = styled(CostDataHeaderCell)`
  grid-area: empty;
`;

export const AmountHeader = styled(CostDataHeaderCell)`
  grid-area: amount;
  text-align: end;
  padding-right: ${(props) => props.theme.spacing.spacing4};
`;

export const NettedOutHeader = styled(CostDataHeaderCell)`
  grid-area: netted-out;
`;

export const LenderPaidHeader = styled(CostDataHeaderCell)`
  grid-area: lender-paid;
`;

export const DeleteHeader = styled(CostDataHeaderCell)`
  grid-area: delete;
`;

interface IAlignedRowProps {
  extendedContent?: React.ReactNode;
  showLenderPaid?: boolean;
  showPayoff?: boolean;
  showSeller?: boolean;
  showNettedOut?: boolean;
}
export const AlignedRow = styled.div<IAlignedRowProps>`
  display: grid;
  grid-template-columns: ${(props) =>
    `${numberColumnWidth} ${nameCellWidth} repeat(${
      props.showSeller ? '5' : '3'
    }, ${columnWidth}) ${narrowColumnWidth} ${wideColumnWidth} ${narrowColumnWidth}`};
  grid-template-areas: ${(props) =>
    `'row-number name buyer-closing buyer-before ${
      props.showSeller ? 'seller-closing seller-before' : ''
    } others netted-out lender-paid delete'
    ${props.extendedContent ? "'" + 'extended '.repeat(8 + (props.showSeller ? 2 : 0)) + "'" : ''}`};
  min-height: ${rowHeight};
  ${(props) =>
    props.showPayoff &&
    `grid-template-columns: ${numberColumnWidth} ${nameCellWidth} ${toColumnWidth} ${payeeColumnWidth} ${
      props.showSeller ? columnWidth + ' ' + columnWidth : ''
    } ${columnWidth} ${wideColumnWidth} ${narrowColumnWidth} ${wideColumnWidth} ${narrowColumnWidth};
      grid-template-areas: 'row-number name to payee ${
        props.showSeller ? 'seller-closing seller-before' : ''
      } empty amount netted-out lender-paid delete';
    `}
`;

interface IRowNumberProps {
  rowNumberDisplay?: string;
  noCounter?: boolean;
}
export const RowNumber = styled(ContentCell)<IRowNumberProps>`
  grid-area: row-number;
  &::before {
    counter-increment: cost-data;
    content: counter(cost-data, decimal-leading-zero);
    ${(props) => (props.rowNumberDisplay === 'empty' ? `counter-increment: cost-data 0; content: normal;` : null)}
  }
  ${(props) =>
    props.noCounter
      ? `&::before {
    counter-increment: cost-data 0;
    content: normal;
  }`
      : null}
`;

interface IContentCellProps {
  noLeftPadding: boolean;
  noRightPadding: boolean;
}
export const NameContent = styled(ContentCell)<IContentCellProps & React.PropsWithChildren<any>>`
  grid-area: name;
  padding: ${(props) =>
    `${props.theme.spacing.spacing2} ${props.theme.spacing.spacing2} ${props.theme.spacing.spacing2} ${props.theme.spacing.spacing4}`};
  & > .typeahead:first-child,
  & > .select:first-child,
  & > .form-cell:first-child,
  & > .faux-input:first-child,
  & > .select.label,
  & > .faux-input:first-child {
    flex: 2;
  }
  & > .faux-input {
    width: auto;
  }
  & > .faux-input[name='payee'] {
    width: 150px;
    flex: 1;
  }
  ${(props) => (props.noLeftPadding ? `padding-left: 0;` : null)}
  ${(props) => (props.noRightPadding ? `padding-right: 0;` : null)}
`;

export const BuyerClosingContent = styled(ContentCell)`
  ${amountContentStyles};
  grid-area: buyer-closing;
  & .faux-input {
    text-align: right;
  }
`;

export const BuyerBeforeContent = styled(ContentCell)`
  ${amountContentStyles};
  grid-area: buyer-before;
`;

export const SellerClosingContent = styled(ContentCell)`
  ${amountContentStyles};
  grid-area: seller-closing;
`;

export const SellerBeforeContent = styled(ContentCell)`
  ${amountContentStyles};
  grid-area: seller-before;
`;

export const OtherContent = styled(ContentCell)`
  ${amountContentStyles};
  grid-area: others;
`;

export const NettedOutContent = styled(ContentCell)`
  grid-area: netted-out;
`;

export const LenderPaidContent = styled(ContentCell)`
  grid-area: lender-paid;
`;

export const DeleteContent = styled(ContentCell)`
  grid-area: delete;
`;

export const ToContent = styled(ContentCell)`
  grid-area: to;
`;

export const AmountContent = styled(ContentCell)`
  grid-area: amount;
`;

export const EmptyContent = styled(ContentCell)`
  grid-area: empty;
`;

export const PayeeContent = styled(ContentCell)`
  grid-area: payee;
`;

export const ExtendedContent = styled(ContentCell)`
  grid-area: extended;
  padding-left: ${numberColumnWidth};
  border-bottom: 1px solid ${(props) => props.theme.colors.gray};
`;
